import { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import {
  MessagesSquare, Plus, Loader2, Users,
  X, UserPlus, Search, Pin, Copy, Check, Lock, Megaphone, ChevronDown,
} from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { Switch } from "./ui/switch";
import { toast } from "sonner";
import { api, fileUrl } from "../lib/api";
import { ChatRoom, ThreadRow, Avatar } from "./CorrChat";
import { BroadcastDialog } from "./corr/BroadcastDialog";
import { WaForwardDialog } from "./WaForwardDialog";
import { personMatch } from "../lib/searchText";

const MGMT_ROLES = ["manager", "hr_manager", "operations_manager"];
const isMgmtRole = (r) => MGMT_ROLES.includes(r);

const QuickAddSupervisorDialog = ({ open, onOpenChange, onAdded, onAdvanced }) => {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);
  useEffect(() => { if (!open) { setEmail(""); setName(""); setResult(null); } }, [open]);
  const submit = async () => {
    const e = email.trim();
    if (!e || !e.includes("@")) { toast.warning("أدخل بريداً إلكترونياً صحيحاً"); return; }
    setSaving(true);
    try {
      const r = await api.inviteCorrespondenceSupervisor(e, [], name.trim());
      const link = `${window.location.origin}${r.portal_path || ""}`;
      setResult({ email: e, password: r.portal_password, link });
      toast.success("تمت إضافة المشرف وإنشاء رابط الدخول");
      onAdded && onAdded();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "تعذّرت إضافة المشرف");
    } finally { setSaving(false); }
  };
  const copyInvite = () => {
    const txt = `رابط بوابة المراسلات:\n${result.link}\n\nالبريد: ${result.email}\nكلمة المرور: ${result.password}`;
    navigator.clipboard?.writeText(txt);
    toast.success("تم نسخ الرابط وبيانات الدخول — أرسلها للمشرف");
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md font-body" dir="rtl" data-testid="corr-quick-add-dialog">
        <DialogHeader className="text-right">
          <DialogTitle className="font-heading text-lg flex items-center gap-2"><UserPlus className="h-5 w-5 text-blue-600" /> إضافة شخص يدوياً</DialogTitle>
          <DialogDescription className="text-right">أدخل البريد الإلكتروني — سيُضاف بدور «مشرف» ويُنشأ له رابط دخول خاص بالمراسلات مع كلمة مرور.</DialogDescription>
        </DialogHeader>
        {result ? (
          <div className="space-y-3" data-testid="corr-invite-result">
            <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-3 space-y-2 text-sm">
              <div className="flex justify-between gap-2"><span className="text-slate-500">البريد</span><span dir="ltr" className="font-semibold">{result.email}</span></div>
              <div className="flex justify-between gap-2"><span className="text-slate-500">كلمة المرور</span><span dir="ltr" className="font-mono-nums font-bold text-emerald-700">{result.password}</span></div>
              <div className="space-y-1"><span className="text-slate-500">رابط البوابة</span>
                <div dir="ltr" className="text-xs bg-white border rounded-lg px-2 py-1.5 break-all">{result.link}</div></div>
            </div>
            <p className="text-[11px] text-amber-600">⚠️ انسخ كلمة المرور الآن — لن تظهر مرة أخرى. لإعادة تعيينها أضف المشرف مجدداً بنفس البريد.</p>
            <Button onClick={copyInvite} className="w-full gap-1.5" data-testid="corr-copy-invite"><Copy className="h-4 w-4" /> نسخ الرابط وبيانات الدخول</Button>
          </div>
        ) : (
        <div className="space-y-1.5">
          <label className="text-xs font-semibold text-slate-500">الاسم</label>
          <Input data-testid="corr-quick-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="اسم الشخص" />
          <label className="text-xs font-semibold text-slate-500">البريد الإلكتروني</label>
          <Input data-testid="corr-quick-email" dir="ltr" type="email" value={email}
            onChange={(e) => setEmail(e.target.value)} placeholder="name@example.com"
            onKeyDown={(e) => { if (e.key === "Enter") submit(); }} />
        </div>
        )}
        {!result && (
        <DialogFooter className="gap-2 sm:gap-2 sm:justify-between">
          {onAdvanced ? (
            <Button variant="ghost" size="sm" onClick={() => { onOpenChange(false); onAdvanced(); }}
              data-testid="corr-quick-advanced" className="gap-1.5 text-slate-500">
              <Users className="h-4 w-4" /> إدارة متقدمة
            </Button>
          ) : <span />}
          <Button onClick={submit} disabled={saving} data-testid="corr-quick-submit" className="gap-1.5">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserPlus className="h-4 w-4" />} إضافة كمشرف
          </Button>
        </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
};

const NewChatDialog = ({ open, onOpenChange, isManagement, canManageUsers, onQuickAdd, onCreated, defaultPrivate }) => {
  const [c, setC] = useState({ supervisors: [], employees: [] });
  const [q, setQ] = useState("");
  const [sel, setSel] = useState([]);
  const [multi, setMulti] = useState(false);
  const [toAll, setToAll] = useState(false);
  const [name, setName] = useState("");
  const [priv, setPriv] = useState(false);
  const [saving, setSaving] = useState("");
  useEffect(() => { if (open) setPriv(!!defaultPrivate); }, [open, defaultPrivate]);
  useEffect(() => {
    if (open) api.correspondenceContacts().then(setC).catch(() => {});
    else { setSel([]); setQ(""); setToAll(false); setName(""); setMulti(false); }
  }, [open]);
  const people = [...(isManagement ? c.supervisors.map((x) => ({ ...x, kind: "supervisors" })) : []), ...c.employees.map((x) => ({ ...x, kind: "employees" }))];
  const s = q.trim();
  const match = (x) => personMatch(x, s);
  const allPeople = people.filter(match);
  const list = allPeople.slice(0, 300);
  const saudis = (c.saudis || []).map((x) => ({ ...x, kind: "employees" })).filter(match);
  const selBtn = (label, arr, tid) => {
    const on = arr.length > 0 && arr.every((x) => sel.some((y) => y.k === `${x.kind}:${x.id}`));
    return (
      <button type="button" disabled={!arr.length} data-testid={`corr-select-${tid}`}
        onClick={() => setSel((p) => {
          const keys = new Set(arr.map((x) => `${x.kind}:${x.id}`));
          if (on) return p.filter((y) => !keys.has(y.k));
          const have = new Set(p.map((y) => y.k));
          return [...p, ...arr.filter((x) => !have.has(`${x.kind}:${x.id}`)).map((x) => ({ ...x, k: `${x.kind}:${x.id}` }))];
        })}
        className={`rounded-full border px-2.5 py-1 text-[11px] font-bold transition-colors disabled:opacity-40 ${on ? "border-emerald-600 bg-emerald-600 text-white" : "border-slate-200 bg-white text-slate-600 hover:bg-slate-100"}`}>
        {on ? "✓ " : ""}{label} ({arr.length})</button>
    );
  };
  const [saOpen, setSaOpen] = useState(false);
  const row = (x) => { const on = sel.some((y) => y.k === key(x)); return (
    <button key={key(x)} type="button" onClick={() => onPick(x)} disabled={!!saving} data-testid={`corr-contact-${x.id}`}
      className={`flex w-full items-center gap-3 px-3 py-2 text-right transition-colors ${on ? "bg-emerald-50" : "hover:bg-slate-50"}`}>
      <Avatar name={x.name} size="h-9 w-9" />
      <div className="min-w-0 flex-1"><p className="truncate text-sm font-semibold">{x.name}</p><p className="truncate text-[11px] text-slate-400">{x.job || (x.kind === "supervisors" ? "مشرف" : "موظف")}{x.branch ? ` · ${x.branch}` : ""}</p></div>
      {saving === key(x) ? <Loader2 className="h-4 w-4 animate-spin text-emerald-600" />
        : multi ? <span className={`flex h-5 w-5 items-center justify-center rounded-full border ${on ? "border-emerald-600 bg-emerald-600 text-white" : "border-slate-300"}`}>{on && <Check className="h-3 w-3" />}</span>
        : <MessagesSquare className="h-4 w-4 text-slate-300" />}
    </button>); };
  const key = (x) => `${x.kind}:${x.id}`;
  const start = async (picked, k) => {
    const sups = picked.filter((x) => x.kind === "supervisors").map((x) => x.id), emps = picked.filter((x) => x.kind === "employees").map((x) => x.id);
    if (!toAll && !sups.length && !emps.length && isManagement) { toast.warning("اختر شخصاً واحداً على الأقل"); return; }
    setSaving(k);
    try {
      const r = await api.createCorrespondenceThread({ subject: name.trim(), to_all: priv ? false : toAll, supervisor_ids: sups, employee_ids: emps, private: priv });
      onOpenChange(false); onCreated(r.id, priv);
    } catch (e) { toast.error(e?.response?.data?.detail || "تعذّر إنشاء المحادثة"); }
    finally { setSaving(""); }
  };
  const onPick = (x) => {
    if (!multi) { start([x], key(x)); return; }
    setSel((p) => (p.some((y) => y.k === key(x)) ? p.filter((y) => y.k !== key(x)) : [...p, { ...x, k: key(x) }]));
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md p-0 overflow-hidden font-body" dir="rtl" data-testid="corr-new-dialog">
        <DialogHeader className="bg-emerald-600 px-4 py-3 text-right text-white">
          <DialogTitle className="font-heading text-lg">{multi ? "مجموعة جديدة" : "محادثة جديدة"}</DialogTitle>
          <DialogDescription className="text-emerald-50 text-right text-xs">{multi ? "اختر الأشخاص ثم اضغط «إنشاء المجموعة»" : "المراسلات للكادر الإداري ومن تتم دعوته — اضغط على الشخص لتبدأ"}</DialogDescription>
        </DialogHeader>
        <div className="space-y-2.5 px-4 pb-4">
          <div className="relative"><Search className="absolute right-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <Input autoFocus data-testid="corr-contact-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="ابحث بالاسم أو الإقامة أو الجوال…" className="h-10 pr-8" /></div>
          <div className="flex gap-1.5">
            <button type="button" onClick={() => { setMulti(!multi); setSel([]); }} data-testid="corr-multi-toggle"
              className={`flex flex-1 items-center justify-center gap-1.5 rounded-xl border py-2 text-xs font-bold transition-colors ${multi ? "border-emerald-600 bg-emerald-50 text-emerald-700" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}><Users className="h-4 w-4" /> مجموعة</button>
            <button type="button" onClick={() => { setPriv(!priv); if (!priv) setToAll(false); }} data-testid="corr-private-switch"
              className={`flex flex-1 items-center justify-center gap-1.5 rounded-xl border py-2 text-xs font-bold transition-colors ${priv ? "border-slate-700 bg-slate-800 text-white" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}><Lock className="h-4 w-4" /> خاصة</button>
          </div>
          {multi && (
            <div className="space-y-2 rounded-xl bg-slate-50 p-2">
              {sel.length > 0 && <div className="flex max-h-24 flex-wrap gap-1.5 overflow-y-auto" data-testid="corr-selected">{sel.map((x) => (
                <span key={x.k} className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2.5 py-1 text-xs font-semibold text-emerald-800">{x.name}
                  <button onClick={() => setSel((p) => p.filter((y) => y.k !== x.k))}><X className="h-3 w-3" /></button></span>))}</div>}
              <Input data-testid="corr-group-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="اسم المجموعة (اختياري)" className="h-9 bg-white" />
              {isManagement && !priv && <label className="flex items-center justify-between text-xs"><span>كل المشرفين</span><Switch checked={toAll} onCheckedChange={setToAll} data-testid="corr-toall-switch" /></label>}
              <div className="flex flex-wrap gap-1.5" data-testid="corr-select-all-bar">
                {selBtn("تحديد الكل", [...allPeople, ...saudis], "all")}
                {isManagement && selBtn("كل الإدارة والمشرفين", allPeople.filter((x) => x.kind === "supervisors"), "sups")}
                {allPeople.some((x) => x.kind === "employees") && selBtn("كل الموظفين", allPeople.filter((x) => x.kind === "employees"), "emps")}
                {saudis.length > 0 && selBtn("كل السعوديين", saudis, "saudis")}
              </div>
            </div>
          )}
          <div className="max-h-[46vh] overflow-y-auto rounded-xl border border-slate-100">
            {list.length === 0 && !saudis.length && <p className="py-6 text-center text-xs text-slate-400">لا يوجد أشخاص — أضف الكادر الإداري من «المستخدمون والصلاحيات» أو ادعُ شخصاً يدوياً</p>}
            {list.map(row)}
            {(c.saudis || []).length > 0 && (
              <div className="border-t border-slate-100" data-testid="corr-saudis-section">
                <button type="button" onClick={() => setSaOpen(!saOpen)} data-testid="corr-saudis-toggle"
                  className="flex w-full items-center justify-between bg-emerald-50/60 px-3 py-2 text-xs font-bold text-emerald-800 transition-colors hover:bg-emerald-50">
                  <span>الموظفون السعوديون ({saudis.length})</span>
                  <ChevronDown className={`h-4 w-4 transition-transform ${saOpen || s ? "rotate-180" : ""}`} />
                </button>
                {(saOpen || s) && (saudis.length ? saudis.map(row) : <p className="py-3 text-center text-xs text-slate-400">لا نتائج</p>)}
              </div>
            )}
          </div>
          {multi && <Button onClick={() => start(sel, "g")} disabled={!!saving || (!sel.length && !toAll)} data-testid="corr-send-thread-button" className="w-full gap-1.5 bg-emerald-600 hover:bg-emerald-700">
            {saving === "g" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Users className="h-4 w-4" />} إنشاء المجموعة ({sel.length})</Button>}
          {canManageUsers && onQuickAdd && (
            <button type="button" onClick={onQuickAdd} data-testid="corr-add-supervisor-inline" className="flex w-full items-center justify-center gap-1.5 text-xs text-slate-500 hover:text-emerald-700"><UserPlus className="h-3.5 w-3.5" /> الشخص غير موجود؟ أضفه يدوياً</button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

const adminAdapter = {
  load: (tid, since) => api.correspondenceMessages(tid, since),
  send: (tid, b) => api.sendCorrespondenceMessage(tid, b),
  upload: (f) => api.uploadFile(f),
  urlFor: (id) => fileUrl(id),
  del: (mid) => api.deleteCorrMessage(mid),
};

export const CorrespondencePanel = ({ user, onUnreadChange, onManageUsers }) => {
  const [threads, setThreads] = useState(null);
  const [active, setActive] = useState(() => { const v = sessionStorage.getItem("corr_open_once"); sessionStorage.removeItem("corr_open_once"); return v; });
  const [newOpen, setNewOpen] = useState(false);
  const [bcOpen, setBcOpen] = useState(false);
  const [quickAddOpen, setQuickAddOpen] = useState(false);
  const [q, setQ] = useState("");
  const [view, setView] = useState("public");
  const [wa, setWa] = useState(null);
  const canWa = ["manager", "hr_manager"].includes(user?.role);
  const openWa = useCallback((m, parts) => setWa({
    text: m?.body || "", attachments: m?.attachments || [],
    ids: (parts || []).filter((p) => String(p.id).startsWith("emp:")).map((p) => p.id.slice(4)),
  }), []);
  const isManagement = isMgmtRole(user?.role);
  const canManageUsers = ["manager", "hr_manager"].includes(user?.role);

  const load = useCallback(() => {
    api.correspondenceThreads(q).then((r) => { setThreads(r.items || []); onUnreadChange && onUnreadChange(); }).catch(() => setThreads([]));
  }, [onUnreadChange, q]);
  useEffect(() => { const id = setTimeout(load, q ? 300 : 0); return () => clearTimeout(id); }, [load, q]);
  useEffect(() => { const iv = setInterval(() => { if (!document.hidden) load(); }, 5000); return () => clearInterval(iv); }, [load]);

  const togglePin = async (t, e) => {
    e.stopPropagation();
    try { await api.pinCorrespondenceThread(t.id, !t.pinned); load(); } catch { toast.error("تعذّر التثبيت"); }
  };
  const delThread = async () => {
    if (!window.confirm("حذف المحادثة بالكامل وكل رسائلها عند الجميع؟ لا يمكن التراجع.")) return;
    try { await api.deleteCorrThread(active); setActive(null); load(); toast.success("تم حذف المحادثة"); }
    catch (e) { toast.error(e?.response?.data?.detail || "تعذّر الحذف"); }
  };
  const cur = (threads || []).find((t) => t.id === active);
  const privCount = (threads || []).filter((t) => t.private).length;
  const unreadCount = (threads || []).filter((t) => t.unread > 0).length;
  const shown = threads && threads.filter((t) => (view === "private" ? t.private : view === "unread" ? t.unread > 0 : true));
  const vCls = (on) => `flex flex-1 items-center justify-center gap-1 rounded-full py-1 text-xs font-semibold transition ${on ? "bg-emerald-600 text-white" : "bg-[#f0f2f5] text-slate-600 hover:bg-slate-200"}`;

  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} data-testid="correspondence-panel"
      className="grid h-[calc(100dvh-330px)] min-h-[440px] grid-cols-1 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm lg:grid-cols-[380px_1fr]">
      <div className={`flex min-h-0 flex-col border-l border-slate-200 ${active ? "hidden lg:flex" : "flex"}`}>
        <div className="flex items-center justify-between bg-[#f0f2f5] px-3 py-2.5">
          <h2 className="font-heading text-lg font-extrabold text-slate-900">المراسلات</h2>
          <div className="flex items-center gap-1.5">
            {isManagement && <button onClick={() => setBcOpen(true)} title="رسالة جماعية للفروع أو المناديب" data-testid="corr-broadcast-button" className="flex items-center gap-1 rounded-full border border-emerald-600 px-3 py-2 text-xs font-bold text-emerald-700 transition-colors hover:bg-emerald-50"><Megaphone className="h-4 w-4" /> جماعية</button>}
            <button onClick={() => setNewOpen(true)} data-testid="corr-new-button" className="flex items-center gap-1.5 rounded-full bg-emerald-600 px-4 py-2 text-sm font-bold text-white shadow transition-[background-color,transform] hover:bg-emerald-700 active:scale-95"><Plus className="h-4 w-4" /> محادثة جديدة</button>
          </div>
        </div>
        <div className="px-3 py-2"><div className="relative"><Search className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input data-testid="corr-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="ابحث في المحادثات والملفات…" className="h-9 rounded-full border-0 bg-[#f0f2f5] pr-9" /></div>
          <div className="mt-2 flex gap-1.5">
            <button onClick={() => setView("public")} className={vCls(view === "public")} data-testid="corr-view-public"><MessagesSquare className="h-3.5 w-3.5" /> الكل ({(threads || []).length})</button>
            <button onClick={() => setView("unread")} className={vCls(view === "unread")} data-testid="corr-view-unread">غير مقروءة ({unreadCount})</button>
            {privCount > 0 && <button onClick={() => setView("private")} className={vCls(view === "private")} data-testid="corr-view-private"><Lock className="h-3.5 w-3.5" /> الخاصة ({privCount})</button>}
          </div></div>
        <div className="min-h-0 flex-1 overflow-y-auto">
          {threads === null ? <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
            : shown.length === 0 ? <div className="px-6 py-16 text-center text-sm text-slate-400" data-testid="corr-empty"><MessagesSquare className="mx-auto mb-3 h-10 w-10 text-slate-300" />{q ? "لا توجد نتائج" : view === "unread" ? "لا توجد رسائل غير مقروءة" : view === "private" ? "لا توجد مراسلات خاصة" : "لا توجد محادثات — اضغط «محادثة جديدة»"}</div>
            : shown.map((t) => <ThreadRow key={t.id} t={t} active={active === t.id} onClick={() => setActive(t.id)}
                right={<span className="inline-flex items-center gap-0.5">{t.private && <Lock className="h-3.5 w-3.5 text-slate-500" data-testid={`corr-private-icon-${t.id}`} />}<span role="button" tabIndex={0} data-testid={`corr-pin-${t.id}`} onClick={(e) => togglePin(t, e)} className={`rounded p-0.5 ${t.pinned ? "text-amber-500" : "text-slate-300 hover:text-slate-500"}`}><Pin className="h-3.5 w-3.5" fill={t.pinned ? "currentColor" : "none"} /></span></span>} />)}
        </div>
      </div>
      <div className={`min-h-0 ${active ? "flex" : "hidden lg:flex"} flex-col`}>
        {active ? (
          <ChatRoom key={active} threadId={active} adapter={adminAdapter} actAs={["manager", "hr_manager"].includes(user?.role)} onBack={() => setActive(null)} onChanged={load}
            title={cur ? (cur.direct ? (cur.peer?.name || cur.participants_label) : cur.subject) : "…"}
            subtitle={cur && !cur.direct ? cur.participants_label : (cur?.direct ? [cur.peer?.kind, cur.peer?.phone, cur.peer?.number && `رقم ${cur.peer.number}`, cur.between && `بين: ${cur.between}`].filter(Boolean).join(" · ") || "محادثة مباشرة" : "")}
            group={cur ? !cur.direct : true} onWa={canWa ? openWa : undefined}
            canDeleteAny={user?.role === "manager"} onDeleteThread={user?.role === "manager" && !cur?.org_group ? delThread : undefined} company={user?.company_name} />
        ) : (
          <div className="flex h-full flex-col items-center justify-center bg-[#f0f2f5] text-slate-400" data-testid="corr-no-selection">
            <MessagesSquare className="mb-3 h-14 w-14 text-slate-300" /><p className="font-semibold text-slate-500">المراسلات المباشرة</p><p className="text-xs">اختر محادثة أو ابدأ محادثة جديدة</p>
            <Button onClick={() => setNewOpen(true)} className="mt-4 gap-1.5 bg-emerald-600 hover:bg-emerald-700" data-testid="corr-empty-new"><Plus className="h-4 w-4" /> محادثة جديدة</Button>
          </div>
        )}
      </div>
      <NewChatDialog open={newOpen} onOpenChange={setNewOpen} isManagement={isManagement} canManageUsers={canManageUsers}
        onQuickAdd={() => { setNewOpen(false); setQuickAddOpen(true); }} onCreated={(id) => { setView("public"); load(); setActive(id); }} defaultPrivate={view === "private"} />
      <WaForwardDialog open={!!wa} onOpenChange={(v) => !v && setWa(null)} text={wa?.text} attachments={wa?.attachments} defaultIds={wa?.ids} />
      <BroadcastDialog open={bcOpen} onOpenChange={setBcOpen} onSent={() => setTimeout(load, 2500)} />
      <QuickAddSupervisorDialog open={quickAddOpen} onOpenChange={setQuickAddOpen} onAdded={load} onAdvanced={onManageUsers} />
    </motion.div>
  );
};
